import { makeAutoObservable, runInAction } from "mobx";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
/** @typedef {import("@shared/typedefs").Tag} Tag */
import { genres, allTags, tagsFlat } from "@shared/constants";
import MusicTrackState from "./components/MusicTrack/MusicTrackState";
import VideoSyncState from "./components/VideoSync/VideoSyncState";
import LoginFlowState from "./components/LoginFlow/LoginFlowState";
import AddToPlaylistState from "./components/AddToPlaylist/AddToPlaylistState";
import MyAccountState from "./components/MyAccount/MyAccountState";
import ShareState from "./components/Share/ShareState";
import SavedSet from "./utils/SavedSet";
/** @typedef {import("../sql/schemaDatatypes").MusicSql} MusicSql */
/** @typedef {import("@shared/musicEndpointTypes").SearchPost} SearchPost */
/** @typedef {import("@shared/musicEndpointTypes").SearchPostResponse} SearchPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").SavedSearchPost} SavedSearchPost */
/** @typedef {import("@shared/musicEndpointTypes").SavedSearchPostResponse} SavedSearchPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").SavedSearchGetResponse} SavedSearchGetResponse */
/** @typedef {import("@shared/musicEndpointTypes").SavedPlaylistPost} SavedPlaylistPost */
/** @typedef {import("@shared/musicEndpointTypes").AutocompletePost} AutocompletePost */
/** @typedef {import("@shared/musicEndpointTypes").AutocompletePostResponse} AutocompletePostResponse */
import UploadTrackState from "./components/UploadTrack/UploadTrackState";
import AdminTrackEditorState from "./components/AdminTrackEditor/AdminTrackEditorState";
import Timeline from "./Timeline";

import retryFetch from "./utils/retryFetch";

let BASE_URL = "https://search.ascheandspencer.com";
let API_BASE_URL = "https://api2.ascheandspencer.com";
let SHORTENER_BASE_URL = "https://a-s.audio";

if (window.location.hostname.includes("localhost")) {
  BASE_URL = "http://localhost:3333";
  API_BASE_URL = "http://localhost:8081";
  SHORTENER_BASE_URL = "http://localhost:8081";
} else if (window.location.hostname.includes("dev.ascheandspencer.com")) {
  BASE_URL = "https://dev.ascheandspencer.com";
  API_BASE_URL = "https://dev.api.ascheandspencer.com";
  SHORTENER_BASE_URL = "https://dev.a-s.audio";
}

export { BASE_URL, API_BASE_URL, SHORTENER_BASE_URL };

export const CLOUDFRONT_BASE_URL = "https://d3i52mr356kz3z.cloudfront.net"; // 'https://d3i52mr356kz3z.cloudfront.net'; // 'https://d33wth2ud6g48.cloudfront.net';
export const MIN_MUSIC_PAGE_SIZE = 18;
export const MAX_MUSIC_PAGE_SIZE = 100;
export const LOAD_NEXT_MUSIC_PAGE_PX = 500;
export const LOADING_INDICATOR_HEIGHT = 110;

// If we're not on prod, delete the ci_session cookie from the parent domain
if (BASE_URL.indexOf("search") === -1) {
  document.cookie =
    "ci_session=" +
    ";expires=Thu, 01 Jan 1970 00:00:01 GMT ;domain=.ascheandspencer.com;path=/";
}

const AUDIBLE_SCRUB_PAUSE = 100;

export const DISABLE_CACHEING = BASE_URL.indexOf("search") === -1;

class SearchState {
  /** @type {Array<Tag>} */
  tags = tagsFlat;

  /** @type {Array<Array<Tag>>} */
  genres = genres;

  /** @type {Array<Array<Tag>>} */
  allTags = allTags;

  /** @type {Array<Tag>} */
  tagsFlat = tagsFlat;

  /** @type {number | null} */
  hoveredGenre = null;

  /** @type {NodeJS.Timeout | null} */
  hoveredGenreTimeout = null;

  /** @type {Array<string>} */
  activeTags = [];

  firstLoad = true;

  language = "en";

  /** @type {"search" | "favorites" | "category" | "reset_password" | "untagged" | "all_disabled" | "similar" | "keyword_search" | "ai_search"} */
  searchType = "search";

  searchId = "";

  /** @type {string} */
  aiSearchQuery = "";

  /** @type {boolean} */
  aiSearchLoading = false;

  /** @type {boolean} */
  aiSearchPanelOpen = false;

  /** @type {Array<{text: string, searchedAt: string}>} */
  aiSearchRecentSearches = [];

  /** @type {boolean} */
  aiSearchIgnoreVocals = false;

  loadingMusic = false;

  /** @type {Array<MusicTrackState>} */
  music = [];

  /** @type {number | null} */
  musicCount = 0;

  hasMoreMusic = false;

  loadingMusicNonce = 1;

  loadingMusicCountNonce = 1;

  // This is to avoid race conditions with multiple loads in-flight
  musicListScrollTop = 0;

  musicListHeight = 500;

  musicListWidth = 600;

  showingVideoSync = true;

  showSyncTip = false;

  /** @type {NodeJS.Timeout | null} */
  syncTipTimeout = null;

  /** @type {VideoSyncState} */
  videoSyncState = new VideoSyncState(this);

  // This is the current music volume
  musicVolume = 1.0;

  // This saves the musicVolume when we're looking at search
  searchMusicVolume = 1.0;

  // This saves the musicVolume when we're looking at video
  videoMusicVolume = 1.0;

  hasAnyLikes = false;

  hasOriginalLikes = false;

  // Track IDs as they were when a saved playlist was first loaded from the server.
  // This helps maintain consistent "save playlist" button visibility even when
  // localStorage is modified by other tabs.
  /** @type {Array<string> | null} */
  playlistOriginalTrackIds = null;

  hasAnyPlayed = false;

  /** @type {MusicTrackState | null} */
  selectedTrack;

  // Search like track popup window state
  /** @type {MusicTrackState | null} */
  searchingLikeTrack = null;

  /** @type {MusicTrackState | null} */
  searchingSimilarTrack = null;

  /** @type {Array<string>} */
  searchingLikeTrackTags = [];

  showingLoginFlow = false;

  /** @type {LoginFlowState} */
  loginFlowState = new LoginFlowState(this);

  /** @type {ShareState} */
  shareState = new ShareState(this);

  showingSharePopup = false;

  showingMyAccount = false;

  /** @type {MyAccountState} */
  myAccountState = new MyAccountState(this);

  /** @type {string | null} */
  shortLink = null;

  /** @type {string | null} */
  shortLinkTrackName = null;

  /** @type {string | null} */
  shortLinkType = "Playlist";

  /** @type {Function | null} */
  tosCallback = null;

  acceptedTOS = false;

  /** @type {boolean} */
  loggedIn;

  menuOpen = false;

  mobileMenuOpen = false;

  mobileProfileMenuOpen = false;

  mobileActive = false;

  showingAllSelectedMobile = false;

  mobileReset = false;

  showingFavoritesMenu = false;

  showingAddToPlaylistPopup = false;

  /** @type {AddToPlaylistState} */
  addToPlaylistState = new AddToPlaylistState(this);

  /** @type {int | null} */
  idToAddToPlaylist;

  showingPlaylistListing = false;

  loginReferrer = false;

  // Timestamp of the last auth status check (throttles 401-triggered checks)
  lastAuthCheckAt = 0;

  /** @type {string | null} */
  email;

  /** @type {number | null} */
  loggedInId;

  /** @type {boolean | null} */
  isAdmin;

  /** @type {HTMLAnchorElement | null} */
  loginLink;

  /** @type {HTMLAnchorElement | null} */
  showAccount;

  searchBarContents = "";

  /** @type {Array<string>} */
  autocompleteTracks = [];

  autocompleteDisabledTrack = false;

  searchBarHasFocus = false;

  autocompleteHighlight = -1;

  autocompleteReplaceContents = false;

  searchBarLoadingAutocomplete = false;

  /** @type {any} */
  searchBarRef = null;

  /** @type {"asc" | "desc" | null} */
  sortDirection = null;

  showingMusicTypePopup = false;

  /** @type {Function | null} */
  musicTypesCallback = null;

  showingClearAllLikesPopup = false;

  /** @type {Function | null} */
  confirmActionCallback = null;

  confirmActionMessage = "";

  /** @type {string | null} */
  tipMessage = null;

  favoritesTipCount = 0;

  keywordsTipCount = 0;

  showingKeywordDefinitions = false;

  showingVideoSyncError = false;

  showingUpload = false;

  uploadTrackState = new UploadTrackState(this);

  showingAdminTrackEditor = false;

  adminTrackEditorState = new AdminTrackEditorState(this);

  showPlaybackTip = false;

  showFavoritesTip = false;

  showListTip = false;

  showingSyncButtonTip = false;

  syncButtonTipCount = 0;

  clickedPlayCount = 0;

  showKeywordsTip = false;

  // Support dragging tracks to re-order
  isDragging = false;

  /** @type {number | null} */
  dragStart = null;

  /** @type {number | null} */
  dragEnd = null;

  /** @type {Timeline} */
  timeline = new Timeline(this);

  /** @type {string | null} */
  playlistName = null;

  /** @type {Object | null} */
  savedPlaylistData = {};

  /** @type {number | null} */
  playlistOwner = null;

  /** @type {number | null} */
  linkedVideoId = null;

  // showingSafariWarning = false;

  showingInitialNote = false;

  hasActiveGenres = false;

  hasActiveKeywords = false;

  showingKeywordsOnMobile = true;

  showingEditKeywordsOnMobile = true;

  showingEditGenresOnMobile = true;

  showingGenresOnMobile = false;

  hasMadeKeywordChange = false;

  hasMadeGenreChange = false;

  genreChildrenPresent = false;

  enableHearts = false;

  isMobileWindow = false;

  isCompactLayout = false;

  /** @type {Array<Tag>} */
  activeTagsWithSubgenres = [];

  /** @type {Boolean} */
  showingPasswordPopup = false;

  /** @type {string | null} */
  tempPassword = null;

  incorrectPassword = false;

  constructor(onSafari) {
    makeAutoObservable(
      this,
      {
        visibleMusic: true,
        showingInstructions: true,
        refreshMusic: true,
      },
      { enforceActions: "observed" }
    );

    // this.showingSafariWarning = onSafari;
    /*
    this.showingInitialNote =
      window.localStorage.getItem("shown_initial_note") == null;
     */

    document.body.classList.add(i18n.language);

    this.refreshLikes();
    this.refreshAnyPlayed();
    this.refreshSearchFromURL();

    this.checkAuthStatus();
    this.startAuthPolling();

    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.onScroll);
    window.addEventListener("keydown", this.keyListener);

    window.dispatchEvent(new Event("resize"));
  }

  keyListener = (e) => {
    if (window.innerWidth < 900) return;
    if (e.key === " ") {
      e.preventDefault();
      e.stopPropagation();

      if (
        this.selectedTrack != null &&
        this.selectedTrack !== MusicTrackState.globallyActiveTrack
      ) {
        this.selectedTrack.play();
      }
      // If there's nothing selected and nothing currently playing
      else if (
        this.selectedTrack == null &&
        MusicTrackState.globallyActiveTrack == null
      ) {
        if (this.visibleMusic.length > 0) {
          this.visibleMusic[0].play();
        }
      } else if (!this.timeline.playing) {
        this.timeline.play();
      } else {
        this.timeline.pause();
      }
    } else if (e.key === "Enter") {
      this.hidePlaybackTip();
      this.closeTip();
      if (this.selectedTrack != null) {
        this.playTrackAfter(this.selectedTrack);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();

      this.hideFavoritesTip();
      if (this.selectedTrack == null) {
        if (this.visibleMusic.length > 0) {
          this.selectTrack(this.visibleMusic[0]);
        }
      } else this.selectTrack(this.getAdjacentTrack(this.selectedTrack, -1));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();

      this.hideFavoritesTip();
      if (this.selectedTrack == null) {
        if (this.visibleMusic.length > 0) {
          this.selectTrack(this.visibleMusic[0]);
        }
      } else this.selectTrack(this.getAdjacentTrack(this.selectedTrack, 1));
    } else if (e.key === "+" || e.key === "=") {
      // Favorite the globally active (NOT selected) track

      if (MusicTrackState.globallyActiveTrack != null) {
        const active = MusicTrackState.globallyActiveTrack;
        if (!active.liked) {
          active.toggleLiked();
        }
      }
    } else if (e.key === "-" || e.key === "_") {
      // Unfavorite the globally active (NOT selected) track

      if (MusicTrackState.globallyActiveTrack != null) {
        const active = MusicTrackState.globallyActiveTrack;
        if (active.liked) {
          active.toggleLiked();
        }
      }
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();

      // Scrub forwards by 1/12th track length on globally active track
      let scrubLength = this.timeline.trackDuration / 12;
      if (this.videoSyncState.url != null) {
        scrubLength = this.timeline.videoDuration / 12;
      }
      this.timeline.setPosition(this.timeline.position + scrubLength);
      if (this.timeline.playing) {
        this.timeline.pause();
        setTimeout(() => {
          this.timeline.play();
        }, AUDIBLE_SCRUB_PAUSE);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();

      // Scrub backwards by 1/12th track length on globally active track
      let scrubLength = this.timeline.trackDuration / 12;
      if (this.videoSyncState.url != null) {
        scrubLength = this.timeline.videoDuration / 12;
      }
      this.timeline.setPosition(this.timeline.position - scrubLength);
      if (this.timeline.playing) {
        this.timeline.pause();
        setTimeout(() => {
          this.timeline.play();
        }, AUDIBLE_SCRUB_PAUSE);
      }
    } else if (e.key === "e") {
      if (this.isAdmin && this.selectedTrack != null) {
        e.preventDefault();
        e.stopPropagation();
        this.showAdminTrackEditor(this.selectedTrack);
      }
      // } else if (e.key === "v") {
      //   if (this.showingVideoSync) {
      //     this.hideVideoSync();
      //   } else if (this.visibleMusic.length > 0) {
      //     this.showVideoSync();
      //   }
    } else if (e.key === "a") {
      if (this.selectedTrack != null) {
        e.preventDefault();
        e.stopPropagation();
        this.showAlternateTracks(this.selectedTrack);
      }
    }
  };

  showAlternateTracks = (track) => {
    if (MusicTrackState.globallyActiveTrack != null) {
      const active = MusicTrackState.globallyActiveTrack;
      active.toggleShowingAlternativeVersions();
    }
  };

  trackBelongsToCurrentPlaylist = (track) => {
    if (this.searchType != "favorites" && this.activeTags.length > 0) {
      return false;
    }
    if (this.music.map((m) => m.music.id).indexOf(track.music.id) === -1) {
      return false;
    }
    if (this.playlistOwner != this.loggedInId) return false;
    return true;
  };

  removeFromCurrentPlaylist = (track) => {
    if (!this.trackBelongsToCurrentPlaylist(track)) return false;
    this.deleteTrack(track);
    this.myAccountState.savePlaylistAfterTrackRemoval();
    return true;
  };

  currentLanguage = i18n.language || "en";

  setCurrentLanguage = (lng) => {
    this.currentLanguage = lng;
  };

  changeLanguage = async (lng) => {
    try {
      await i18n.changeLanguage(lng);
      // Update observable to trigger re-renders in observer components
      // Use action to modify the observable
      this.setCurrentLanguage(lng);
      // The language change is successful and UI updates automatically
      // No need to reload - React will re-render with new translations
      localStorage.setItem("i18nextLng", lng); // Persist for next visit
    } catch (error) {
      console.error("Error changing language:", error);
      // Only reload if there's an error as a fallback
      localStorage.setItem("i18nextLng", lng);
      window.location.reload();
    }
  };

  hidePlaylistPopup = () => {
    this.showingAddToPlaylistPopup = false;
  };

  closeSafariWarning = () => {
    this.showingSafariWarning = false;
  };

  closeInitialNote = () => {
    this.showingInitialNote = false;
    window.localStorage.setItem("shown_initial_note", "true");
  };

  hideIncorrectPassword = () => {
    this.incorrectPassword = false;
  };

  showIncorrectPassword = () => {
    this.incorrectPassword = true;
  };

  setPasswordForPlaylist = (value) => {
    this.tempPassword = value;
  };

  showPasswordPrompt = () => {
    this.showingPasswordPopup = true;
  };

  closePasswordPopup = () => {
    this.showingPasswordPopup = false;
  };

  checkPassword = () =>
    retryFetch(`${API_BASE_URL}/api/v1/check-password`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify({
        playlistId: this.searchId,
        password: this.tempPassword,
      }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        // console.log(response);
        if (response.success) {
          window.localStorage.setItem(`playlist-${this.searchId}`, "true");
          this.setPasswordForPlaylist(null);
          this.hideIncorrectPassword();
          this.closePasswordPopup();
        } else {
          this.showIncorrectPassword();
        }
      });

  changeMobileWindow = (isMobile) => {
    this.isMobileWindow = isMobile;
    this.isCompactLayout = window.innerWidth < 1220;
  };

  logEvent = (what, act, name) => {
    // console.log(what, act, name);
    ReactGA.event({
      category: what,
      action: act,
      label: name,
    });
  };

  /**
   * Returns a list of tracks to display that removes all siblings, unless you're an admin
   */
  get visibleMusic() {
    const visibleMusic = [];
    if (this.searchType === "favorites" || this.searchType === "all_disabled") {
      for (let i = 0; i < this.music.length; i += 1) {
        visibleMusic.push(this.music[i]);
      }
    } else {
      const seenSharedIds = [];
      for (let i = 0; i < this.music.length; i += 1) {
        const versionSearchTerm = this.music[i].music.version_search_term;
        if (
          versionSearchTerm == null ||
          seenSharedIds.indexOf(versionSearchTerm) === -1
        ) {
          if (versionSearchTerm != null) {
            seenSharedIds.push(versionSearchTerm);
          }
          visibleMusic.push(this.music[i]);
        }
      }
    }
    if (this.isDragging && this.dragStart !== this.dragEnd) {
      const draggedTrack = visibleMusic[this.dragStart];
      visibleMusic.splice(this.dragStart, 1);
      visibleMusic.splice(this.dragEnd, 0, draggedTrack);
    }
    return visibleMusic;
  }

  showDownloadSearch = () => {
    if (this.playlistName != null) return true;
    const tagCount = window.location.href.split("#").length;
    if (tagCount > 1) {
      const tag = window.location.href.split("#")[1];
      if (tag.indexOf("shared=1") !== -1) return true;
    }
    return false;
  };

  allowDrag = () =>
    !this.isMobileWindow &&
    this.searchType === "favorites" &&
    this.sortDirection == null &&
    this.loggedIn;

  /**
   * @action
   * @param {number} index
   */
  startDrag = (index) => {
    this.isDragging = true;
    this.dragStart = index;
    this.dragEnd = index;
  };

  /**
   * @action
   * @param {number} index
   */
  mouseOver = (index) => {
    if (this.isDragging) {
      this.dragEnd = index;
    }
  };

  cancelDrag = () => {
    this.isDragging = false;
  };

  dropDrag = () => {
    this.isDragging = false;
    const dragged = this.visibleMusic[this.dragStart];
    const dropped = this.visibleMusic[this.dragEnd];
    const indexOfDragged = this.music.indexOf(dragged);
    const indexOfDropped = this.music.indexOf(dropped);
    if (indexOfDragged > -1 && indexOfDropped > -1) {
      this.music.splice(indexOfDragged, 1);
      this.music.splice(indexOfDropped, 0, dragged);
    }

    /** @type {SavedPlaylistPost} */ const savedPlaylist = {
      id: this.searchId,
      tracks: this.music.map((m) => m.music.id),
      name: this.playlistName,
    };

    retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id === -1) {
          alert(
            r.error === "not_logged_in"
              ? "You are not logged in. Please log in and try again."
              : "Failed to save favorites. Please try again."
          );
          return;
        }
        const url = `/en/favorites/${r.id}`;
        window.history.pushState({}, "", url);
      });
  };

  /**
   * @action
   * @param {number} dragIndex
   */
  moveTrackToTop = (dragIndex) => {
    this.dragStart = dragIndex;
    this.dragEnd = 0;
    this.isDragging = true;
    this.dropDrag();
  };

  /**
   * @action
   * @param {MusicTrackState} deleteTrack
   */
  deleteTrack = (track) => {
    this.music = this.music.filter((m) => m !== track);
    if (this.selectedTrack === track) {
      this.selectedTrack = null;
    }
  };

  /**
   * @action
   * @param {MusicTrackState} showAdminTrackEditor
   */
  showAdminTrackEditor = (track) => {
    this.showingAdminTrackEditor = true;
    this.selectTrack(track);
    this.adminTrackEditorState.focus();
  };

  hideAdminTrackEditor = () => {
    this.showingAdminTrackEditor = false;
  };

  showUploadWindow = () => {
    this.showingUpload = true;
  };

  hideUploadWindow = () => {
    this.showingUpload = false;
    this.uploadTrackState.reset();
  };

  /**
   * Sets the search bar contents and triggers autocomplete
   * @action
   * @param {string} contents - The search text content
   */
  setSearchBarContents = (contents) => {
    this.searchBarContents = contents;
    this.refreshSearchBarAutocomplete();
  };

  /**
   * Refreshes the search bar autocomplete suggestions
   * @action
   */
  refreshSearchBarAutocomplete = () => {
    if (this.searchBarContents.length > 0) {
      this.searchBarLoadingAutocomplete = true;
      /** @type {AutocompletePost} */
      const savedPlaylist = {
        query: this.searchBarContents,
      };
      fetch(`${API_BASE_URL}/api/v1/autocomplete`, {
        method: "POST",
        cache: DISABLE_CACHEING ? "reload" : "default",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(savedPlaylist),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((r) => {
          this.searchBarLoadingAutocomplete = false;
          /** @type {string[]} */
          const availableResponses = r
            .filter((s) => s.is_available_to_license)
            .map((s) => s.name);
          // this.autocompleteTracks =
          //   availableResponses.length > 0 ? availableResponses : [];
          this.autocompleteTracks =
            availableResponses.length > 0 ? [availableResponses[0]] : []; // clip down to just first entry
          this.autocompleteDisabledTrack =
            availableResponses.length === 0 && r.length > 0;
          this.autocompleteHighlight = -1;
          this.autocompleteReplaceContents = false;
          console.log(this.autocompleteTracks);
        });
    } else {
      this.autocompleteTracks = [];
    }
  };

  removeAutocompleteTracks = () => {
    console.log("removing autocomplete");
    this.searchBarContents = "";
    this.autocompleteTracks = [];
  };

  /**
   * @action
   * @param {boolean} searchBarHasFocus
   */
  setSearchBarHasFocus = (searchBarHasFocus) => {
    this.searchBarHasFocus = searchBarHasFocus;
  };

  /**
   * @action
   * @param {number} autocompleteHighlight
   */
  setAutocompleteHighlight = (autocompleteHighlight) => {
    this.autocompleteHighlight = autocompleteHighlight;
    this.autocompleteReplaceContents = false;
  };

  moveAutocompleteHighlightDown = () => {
    if (this.autocompleteTracks.length > 0) {
      this.autocompleteHighlight += 1;
      this.autocompleteReplaceContents = true;
      if (this.autocompleteHighlight >= this.autocompleteTracks.length) {
        this.autocompleteHighlight = -1;
        this.autocompleteReplaceContents = false;
      }
    } else {
      this.autocompleteHighlight = -1;
      this.autocompleteReplaceContents = false;
    }
  };

  moveAutocompleteHighlightUp = () => {
    if (this.autocompleteTracks.length > 0) {
      this.autocompleteHighlight -= 1;
      this.autocompleteReplaceContents = true;
      if (this.autocompleteHighlight < -1) {
        this.autocompleteHighlight = this.autocompleteTracks.length - 1;
        this.autocompleteReplaceContents = false;
      }
    } else {
      this.autocompleteHighlight = -1;
      this.autocompleteReplaceContents = false;
    }
  };

  /**
   * @action
   * @param {string} text
   */
  doKeywordSearch = (text) => {
    if (text !== this.searchBarContents) {
      this.searchBarContents = text;
      this.refreshSearchBarAutocomplete();
    }

    const newUrl = `/${this.language}/keyword_search/${
      this.sortDirection != null ? this.sortDirection : ""
    }?${encodeURIComponent(text)}`;
    window.history.pushState({}, "", newUrl);

    this.refreshSearchFromURL();

    if (this.searchBarRef != null) {
      this.searchBarRef.blur();
    }
  };

  /**
   * Clear AI search state when navigating away from AI search
   * @action
   */
  clearAiSearchState = () => {
    this.aiSearchQuery = "";
  };

  /**
   * Open the AI search panel
   * @action
   */
  openAiSearchPanel = () => {
    this.aiSearchPanelOpen = true;
    if (this.loggedIn) {
      this.fetchAiSearchRecents();
    }
  };

  /**
   * Close the AI search panel
   * @action
   */
  closeAiSearchPanel = () => {
    this.aiSearchPanelOpen = false;
  };

  /**
   * Fetch recent AI searches for logged-in user
   * @action
   */
  fetchAiSearchRecents = () => {
    retryFetch(`${API_BASE_URL}/api/v1/ai/recent-searches`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((result) => {
        runInAction(() => {
          if (result.success && result.searches) {
            this.aiSearchRecentSearches = result.searches;
          }
        });
      })
      .catch(() => {});
  };

  /**
   * Perform AI natural language search
   * @action
   * @param {string} query - Natural language search query
   */
  doAiSearch = (query) => {
    this.aiSearchPanelOpen = false;
    this.mobileActive = false;
    if (!query || query.trim().length === 0) return;
    const trimmedQuery = query.trim();

    // Stop any currently playing track
    if (MusicTrackState.globallyActiveTrack != null) {
      const active = MusicTrackState.globallyActiveTrack;
      if (active.playing) active.pause();
      this.timeline.reset();
      MusicTrackState.globallyActiveTrack = null;
    }
    this.selectedTrack = null;

    // Clear keyword/tag search state
    this.activeTags = [];
    this.searchBarContents = "";
    this.playlistName = null;
    this.playlistOriginalTrackIds = null;
    this.searchingLikeTrack = null;
    this.searchingLikeTrackTags = [];
    this.searchingSimilarTrack = null;
    this.highestTags = [];

    this.aiSearchQuery = trimmedQuery;
    this.searchType = "ai_search";
    this.searchId = trimmedQuery;

    const newUrl = `/${this.language}/ai_search/?${encodeURIComponent(trimmedQuery)}`;
    window.history.pushState({}, "", newUrl);

    this.performAiSearch();

    // Clear the input field after dispatching the search
    this.aiSearchQuery = "";
  };

  /**
   * Execute the AI search API call
   * Fetches all results at once (sorted by quality_score, filtered by threshold)
   * @action
   */
  performAiSearch = () => {
    if (!this.aiSearchQuery || this.aiSearchQuery.trim().length === 0) return;

    this.music = [];
    this.musicCount = 0;
    this.aiSearchLoading = true;
    this.loadingMusic = true;
    this.hasMoreMusic = false;

    const loadingMusicNonce = this.loadingMusicNonce + 1;
    this.loadingMusicNonce = loadingMusicNonce;

    retryFetch(`${API_BASE_URL}/api/v1/ai/search`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      credentials: "include",
      body: JSON.stringify({
        text: this.aiSearchQuery,
        ignoreVocals: this.aiSearchIgnoreVocals || undefined,
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        runInAction(() => {
          // Check if we got preempted by another search
          if (this.loadingMusicNonce !== loadingMusicNonce) {
            return;
          }

          this.aiSearchLoading = false;
          this.loadingMusic = false;

          if (result.success && result.tracks) {
            const newTracks = result.tracks.map((track) => new MusicTrackState(track, this));
            this.music = newTracks;
            this.musicCount = result.totalCount || result.tracks.length;
            this.hasMoreMusic = false; // All results loaded at once
            this.refreshLikes();
            console.log(`AI Search: ${this.music.length} tracks loaded (sorted by quality_score)`);
          } else {
            console.error("AI Search failed:", result.error);
            this.music = [];
            this.musicCount = 0;
            this.hasMoreMusic = false;
          }
        });
      })
      .catch((error) => {
        runInAction(() => {
          if (this.loadingMusicNonce !== loadingMusicNonce) {
            return;
          }
          this.aiSearchLoading = false;
          this.loadingMusic = false;
          this.hasMoreMusic = false;
          console.error("AI Search error:", error);
        });
      });
  };

  /**
   * @action
   * @param {string} email
   * @param {boolean} [isAdmin] - Optional
   * @param {number} [id=0] - Optional, defaults to 0
   */
  setLoggedInUser = (email, isAdmin, id) => {
    this.loggedIn = email != null;
    this.email = email;
    this.isAdmin = isAdmin;
    this.loggedInId = id;
    if (this.loggedIn) {
      this.myAccountState.refreshSavedTracks();
      if (this.loginLink != null) this.loginLink.style.display = "none";
      if (this.showAccount != null) this.showAccount.style.display = "";
    } else {
      if (this.loginLink != null) this.loginLink.style.display = "";
      if (this.showAccount != null) this.showAccount.style.display = "none";
      this.email = null;
    }

    if (this.loginReferrer === true) {
      if (this.loggedIn) {
        this.showMyAccount();
      } else {
        this.showLoginFlow();
      }
      this.loginReferrer = false;
    }
  };

  /**
   * Checks the login session with the server and updates the state.
   * Alerts once when a session that was valid has expired.
   * @action
   */
  checkAuthStatus = () => {
    this.lastAuthCheckAt = Date.now();
    return retryFetch(`${API_BASE_URL}/api/v1/auth/status`, {
      method: "GET",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
      cache: DISABLE_CACHEING ? "reload" : "default",
    })
      .then((r) => r.json())
      .then((r) => {
        const wasLoggedIn = this.loggedIn;
        if (r.loggedIn === false) {
          this.setLoggedInUser(null);
        } else {
          this.setLoggedInUser(r.email, r.isAdmin, r.id);
        }
        if (wasLoggedIn && !this.loggedIn) {
          alert("Your login session has expired. Please log in again.");
        }
      });
  };

  /**
   * Keeps the login state fresh in long-lived tabs: re-checks the session
   * every few minutes, when the tab becomes visible again, and after any
   * API call answers 401.
   */
  startAuthPolling = () => {
    setInterval(this.checkAuthStatus, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.checkAuthStatus();
      }
    });
    window.addEventListener("api-unauthorized", () => {
      // Throttle: a burst of failed calls must not spam auth checks.
      if (Date.now() - this.lastAuthCheckAt > 30000) {
        this.checkAuthStatus();
      }
    });
  };

  showLoginFlow = () => {
    this.showingLoginFlow = true;
  };

  hideLoginFlow = () => {
    this.showingLoginFlow = false;
  };

  showMyAccount = () => {
    this.showingMyAccount = true;
    this.myAccountState.refreshSavedTracks();
    this.myAccountState.showSearchFilter();
  };

  togglePlaylistListing = () => {
    this.showingPlaylistListing = !this.showingPlaylistListing;
    window.scrollTo(0, 0);
  };

  toggleFavoritesMenu = () => {
    this.showingFavoritesMenu = !this.showingFavoritesMenu;
  };

  toggleSharePopup = () => {
    this.showingSharePopup = !this.showingSharePopup;
  };

  toggleShowAllSelectedMobile = () => {
    this.showingAllSelectedMobile = !this.showingAllSelectedMobile;
  };

  toggleShowAddToPlaylist = (musicId) => {
    this.idToAddToPlaylist = musicId;
    this.showingAddToPlaylistPopup = !this.showingAddToPlaylistPopup;
    console.log(this.showingAddToPlaylistPopup);
    console.log(this.idToAddToPlaylist);
  };

  hideMyAccount = () => {
    this.showingMyAccount = false;
    this.myAccountState.resetSearchFilter();
  };

  /**
   * @action
   * @param {MusicTrackState} track
   * @param {number} dir
   */
  getAdjacentTrack = (track, dir) => {
    // Move the selected track down
    let index = this.visibleMusic.indexOf(track);

    // If this is a sub-track, check if we're in a parent
    if (track.parent != null) {
      const visibleSiblings = track.parent.getVisibleChildren();
      const siblingIndex = visibleSiblings.indexOf(track);
      // We're not visible anymore, reset to the top of the music
      if (siblingIndex === -1) {
        return track.parent;
      }
      // Move within the siblings, if appropriate
      if (
        siblingIndex + dir >= 0 &&
        siblingIndex + dir < visibleSiblings.length
      ) {
        return visibleSiblings[siblingIndex + dir];
      }
      // If we're going up, maybe exit the children
      if (dir < 0) {
        return track.parent;
      }
      // Move around globally

      index = this.visibleMusic.indexOf(track.parent);
    }

    // If the selected track is no longer on the page, or is null, start at the top
    if (index === -1) {
      if (this.visibleMusic.length > 0) return this.visibleMusic[0];
      return track;
    }
    const visibleChildren = track.getVisibleChildren();
    // Move into children
    if (dir > 0 && visibleChildren.length > 0) {
      return visibleChildren[0];
    }
    // Otherwise move around globally
    if (index + dir >= 0 && index + dir < this.visibleMusic.length) {
      const candidate = this.visibleMusic[index + dir];
      const candidateVisibleChildren = candidate.getVisibleChildren();
      // Check if we need to move into the candidate's children
      if (candidateVisibleChildren.length > 0 && dir < 0) {
        // Return the last element
        return candidateVisibleChildren[candidateVisibleChildren.length - 1];
      }
      // Otherwise, just return the track
      return candidate;
    }
    // If we're at an edge and can't go any further

    return track;
  };

  ensureNextTrackLoaded = () => {
    // If the active track is also selected, just play the next track
    if (
      this.selectedTrack != null &&
      this.selectedTrack === MusicTrackState.globallyActiveTrack
    ) {
      const nextTrack = this.getAdjacentTrack(this.selectedTrack, 1);
      if (nextTrack != null) nextTrack.ensureLoaded();
    }
    // Otherwise just play the selected track
    else if (this.selectedTrack != null) {
      this.selectedTrack.ensureLoaded();
    }
  };

  playNextTrack = () => {
    // If the active track is also selected, just play the next track
    if (
      this.selectedTrack != null &&
      this.selectedTrack === MusicTrackState.globallyActiveTrack
    ) {
      this.playTrackAfter(this.selectedTrack);
    }
    // Otherwise just play the selected track
    else if (this.selectedTrack != null) {
      this.selectedTrack.play(false);
    }
  };

  playTrackAfter = (track) => {
    const nextTrack = this.getAdjacentTrack(track, 1);
    if (nextTrack !== track) {
      nextTrack.play(false);
    }
  };

  reactivateReset = () => {
    this.mobileReset = false;
  };

  resetKeywords = () => {
    this.mobileReset = false;
    this.activeTags = [];
    this.searchId = "";
    this.loadingMusic = false;
    this.hasMoreMusic = false;
    this.music = [];
    this.searchBarContents = "";
    this.searchType = "search";
    this.playlistName = null;
    this.playlistOriginalTrackIds = null;
    this.timeline.reset();
    this.clearAiSearchState();
    window.history.pushState({}, "", "/en/search");

    if (MusicTrackState.globallyActiveTrack != null) {
      const activeTrack = MusicTrackState.globallyActiveTrack;
      if (activeTrack.playing) activeTrack.pause();
      MusicTrackState.globallyActiveTrack = null;
    }
    if (window.innerWidth < 1024) {
      this.isMobileWindow = true;
    } else {
      this.isMobileWindow = false;
    }
    this.isCompactLayout = window.innerWidth < 1220;
    if (this.isMobileWindow) {
      this.musicCount = 0;
      this.mobileMenuOpen = true;
      this.mobileActive = true;
      this.mobileReset = true;
    }
  };

  hasAnyTags = () => this.activeTags.length > 0;

  refreshLikes = () => {
    const likes = SavedSet.getSetContents("tracks");
    this.hasAnyLikes = likes.length > 0;

    // IMPORTANT: This is explicitly NOT this.visibleMusic.length, because we need to account for
    // children also having been liked. If children were liked they will have been returned as top
    // level in the this.music list, and post-processed out
    const musicIds = this.music.map((m) => m.music.id.toString());
    musicIds.sort();
    
    // For saved favorites playlists, compare against the original track IDs from when the
    // playlist was loaded. This prevents multi-tab localStorage changes from breaking the
    // "save playlist" button visibility.
    if (this.searchType === "favorites" && this.playlistOriginalTrackIds != null) {
      const originalIds = [...this.playlistOriginalTrackIds].sort();
      this.hasOriginalLikes = JSON.stringify(musicIds) === JSON.stringify(originalIds);
    } else {
      likes.sort();
      this.hasOriginalLikes = JSON.stringify(musicIds) === JSON.stringify(likes);
    }
  };

  refreshAnyPlayed = () => {
    this.hasAnyPlayed = SavedSet.getSetContents("played").length > 0;
  };

  clearAllLikes = () => {
    this.showConfirmActionCallback(
      "Do you really want to clear all your saved tracks?",
      () => {
        // Turn off everything we can see, to update the UI
        this.music.forEach((m) => {
          if (m.liked) m.toggleLiked();
        });
        // Clear the saved set of likes, to catch any likes we can't see
        SavedSet.clearSet("tracks");
        this.refreshLikes();
      },
    );
    /*
    if (window.confirm('Do you really want to clear ALL your saved tracks?')) {
      // Turn off everything we can see, to update the UI
      this.music.forEach(m => {
        if (m.liked) m.toggleLiked();
      });
      // Clear the saved set of likes, to catch any likes we can't see
      SavedSet.clearSet('tracks');
      this.refreshLikes();
    }
     */
  };

  likeAllResultsAdmin = () => {
    this.visibleMusic.forEach((m) => {
      if (!m.liked) m.toggleLiked();
    });
  };

  markAllPlayed = () => {
    this.music.forEach((m) => {
      m.hasPlayed = true;
      SavedSet.setMembership("played", m.music.id.toString(), true);
      // if (m.children != null) {
      //   for (let i = 0; i < m.children.length; i += 1) {
      //     m.children[i].hasPlayed = true;
      //     SavedSet.setMembership("played", m.children[i].music.id.toString(), true);
      //   }
      // }
    });

    this.refreshAnyPlayed();
  };

  clearAllPlayed = () => {
    // Turn off everything we can see, to update the UI
    this.music.forEach((m) => {
      m.hasPlayed = false;
      if (m.children != null) {
        for (let i = 0; i < m.children.length; i += 1) {
          m.children[i].hasPlayed = false;
        }
      }
    });
    // Clear the saved set of played tracks
    SavedSet.clearSet("played");
    this.refreshAnyPlayed();
  };

  resetSearch = () => {
    this.showConfirmActionCallback(
      "This will clear favorites, reset keywords, clear waveforms and remove a loaded video. Do you want to proceed?",
      () => {
        // Clear all favorites (internal logic without popup)
        this.music.forEach((m) => {
          if (m.liked) m.toggleLiked();
        });
        SavedSet.clearSet("tracks");
        this.refreshLikes();
        
        // Reset keywords and search state
        this.resetKeywords();
        
        // Clear all waveforms (played tracks)
        this.music.forEach((m) => {
          m.hasPlayed = false;
          if (m.children != null) {
            for (let i = 0; i < m.children.length; i += 1) {
              m.children[i].hasPlayed = false;
            }
          }
        });
        SavedSet.clearSet("played");
        this.refreshAnyPlayed();
        
        // Remove loaded video but keep VideoSync visible
        this.videoSyncState.resetVideo();
      }
    );
  };

  showVideoSync = () => {
    if (MusicTrackState.globallyActiveTrack != null) {
      const activeTrack = MusicTrackState.globallyActiveTrack;
      if (activeTrack.playing) activeTrack.pause();
    } else if (this.visibleMusic.length > 0) {
      this.selectTrack(this.visibleMusic[0]);
    }
    this.showingVideoSync = true;
    this.setMusicVolume(this.videoMusicVolume);
    this.timeline.setPosition(this.timeline.position);
  };

  hideVideoSync = () => {
    if (this.videoSyncState.playing) this.videoSyncState.pause();
    this.showingVideoSync = false;
    this.setMusicVolume(this.searchMusicVolume);
    this.timeline.setPosition(this.timeline.position);
    this.videoSyncState.resetVideo();
  };

  /**
   * @action
   * @param {number} musicVolume
   */
  setMusicVolume = (musicVolume) => {
    musicVolume = Math.min(Math.max(musicVolume, 0), 1);
    this.musicVolume = musicVolume;
    if (this.showingVideoSync) {
      this.videoMusicVolume = musicVolume;
    } else {
      this.searchMusicVolume = musicVolume;
    }
    // Always keep the loaded playlist data in sync, otherwise "save data"
    // drops music level changes made outside the video-sync view.
    if (Object.keys(this.savedPlaylistData).length) {
      this.savedPlaylistData.musicVolume = this.musicVolume;
    }
    this.music.forEach((music) => {
      if (music.gainNode != null) music.gainNode.gain.value = this.musicVolume;
      if (music.children != null) {
        for (let i = 0; i < music.children.length; i += 1) {
          if (music.children[i].gainNode != null) {
            music.children[i].gainNode.gain.value = this.musicVolume;
          }
        }
      }
    });
  };

  toggleBurgerMenu = () => {
    this.menuOpen = !this.menuOpen;
  };

  hideMobileMenu = () => {
    this.mobileMenuOpen = false;
  };

  toggleProfileMenu = () => {
    if (!this.loggedIn) {
      this.showLoginFlow();
      return;
    }
    this.mobileProfileMenuOpen = !this.mobileProfileMenuOpen;
  };

  showGenresMobile = () => {
    this.showingGenresOnMobile = true;
    this.showingKeywordsOnMobile = false;
  };

  showKeywordsMobile = () => {
    this.showingGenresOnMobile = false;
    this.showingKeywordsOnMobile = true;
  };

  // toggleGenresMobile = () => {
  //   this.showingGenresOnMobile = !this.showingGenresOnMobile;
  //   if (this.showingGenresOnMobile == true) {
  //     this.showingEditGenresOnMobile = false;
  //   } else {
  //     this.showingEditGenresOnMobile = true;
  //   }
  //   this.triggerScroll();
  // };

  removeGenreChildrenPresent = () => {
    this.genreChildrenPresent = false;
  };

  triggerScroll = () => {
    console.log("trigger scroll");
    setTimeout(() => {
      window.dispatchEvent(new Event("scroll"));
    }, 100);
  };

  setHoveredGenre = (id) => {
    try {
      clearTimeout(this.hoveredGenreTimeout);
    } catch (e) {}
    this.hoveredGenre = id;
  };

  /**
   * @action
   * @param {string} id
   */
  toggleGenreHover = (id) => {
    try {
      clearTimeout(this.hoveredGenreTimeout);
    } catch (e) {}
    this.hoveredGenreTimeout = setTimeout(() => {
      this.setHoveredGenre(id);
    }, 500);
  };

  /**
   * @action
   * @param {string} id
   */
  isGenreHovered = (id) => {
    if (this.hoveredGenre === id) {
      return true;
    }
    return false;
  };

  /**
   * @action
   * @param {string} id
   */
  toggleTagActive = (id) => {
    // Deal with genres
    // console.log("this.genreChildrenPresent", this.genreChildrenPresent);
    this.reactivateReset();

    if (this.genreChildrenPresent === true) {
      this.removeGenreChildrenPresent();
    } else {
      this.hasActiveGenres = false;
    }

    const isActive = this.activeTags.indexOf(id) !== -1;

    // somehow doesn't work?
    // const genre: ?Tag = this.getGenresWithSubgenres().filter(
    //   t => t.id === id
    // )[0];
    /** @type {Tag | null} */ const genre = this.genres
      .flatMap((g) => g)
      .filter((t) => t.id === id)[0]; // works?
    const parentGenre = this.genres
      .flatMap((g) => g)
      .filter(
        (g) =>
          g.children != null &&
          g.children.filter((c) => c.id === id).length > 0,
      )[0];

    // console.log(`Toggling tag ${id}, isActive=${isActive}`);
    if (isActive) {
      // 1. Handle top-level genre deactivation logic

      if (genre != null) {
        // 1.1. Deal with special cases around genres with children
        // If this genre has child genres, deactivate all of those as well

        if (genre.children != null) {
          console.log(`Deactivating genre ${id} with children`);
          const childIds = genre.children.map((child) => child.id);
          this.activeTags = this.activeTags.filter((filterId) => {
            if (childIds.indexOf(filterId) !== -1) return false;
            if (filterId === id) return false;
            return true;
          });
        }

        // 1.3. Handle genres with neither parents nor children
        else {
          this.activeTags.splice(this.activeTags.indexOf(id), 1);
        }
      }

      // 2. Handle genres with parents
      else if (parentGenre != null) {
        /** @type {Array<Tag>} */ const siblings = parentGenre.children;

        let allSiblingsActive = true;
        for (let i = 0; i < siblings.length; i += 1) {
          const sibling = siblings[i];
          if (this.activeTags.indexOf(sibling.id) === -1) {
            allSiblingsActive = false;
          }
        }
        if (this.activeTags.indexOf(parentGenre.id) === -1) {
          allSiblingsActive = false;
        }

        let noSiblingsActive = true;
        for (let i = 0; i < siblings.length; i += 1) {
          const sibling = siblings[i];
          if (this.activeTags.indexOf(sibling.id) !== -1 && sibling.id !== id) {
            noSiblingsActive = false;
          }
        }
        if (this.activeTags.indexOf(parentGenre.id) !== -1) {
          noSiblingsActive = false;
        }

        // 3.2.1. If all the siblings are currently active, then remove them all and
        // keep just ourselves
        // If all of our siblings are active, then make sure we deactivate them
        // all and the parent, and leave only ourself

        if (allSiblingsActive) {
          console.log(
            `Deactivating genre ${id} with parent with all siblings active`,
          );
          const siblingIds = siblings.map((s) => s.id);
          this.activeTags = this.activeTags.filter((filterId) => {
            if (siblingIds.indexOf(filterId) !== -1 && id !== filterId) {
              return false;
            }
            if (filterId === parentGenre.id) {
              return false;
            }
            return true;
          });
        }

        // 1.2.1. If we were the last sibling to be deactivated, just remove it
        // Parent will auto-deselect via isTagActive logic if it wasn't explicitly selected
        else if (noSiblingsActive) {
          console.log(
            `Deactivating genre ${id} with parent with no siblings active`,
          );
          this.activeTags.splice(this.activeTags.indexOf(id), 1);
        }

        // 3.2.2. Otherwise we're not in a boundary case, and can safely remove ourselves as usual
        else {
          console.log(
            `Deactivating genre ${id} with parent with not all siblings active`,
          );
          this.activeTags.splice(this.activeTags.indexOf(id), 1);
        }
      }

      // 2. Handle tag deactivation logic
      else {
        this.activeTags.splice(this.activeTags.indexOf(id), 1);
      }
    }
    // 3. Handle genre activation logic
    else if (genre != null) {
      // 3.1. If this genre has child tags, activate all of those as well

      if (genre.children != null) {
        // this.showingSubgenresOnMobile = true;

        const childIds = genre.children.map((child) => child.id);
        let anyChildrenActive = false;
        for (let i = 0; i < childIds.length; i += 1) {
          const childId = childIds[i];
          if (this.activeTags.indexOf(childId) !== -1) {
            anyChildrenActive = true;
            break;
          }
        }

        if (anyChildrenActive) {
          console.log(
            `Activating genre ${id} with active children = deactivating`,
          );
          this.activeTags = this.activeTags.filter((tagId) => {
            if (childIds.indexOf(tagId) !== -1) return false;
            if (tagId === id) return false;
            return true;
          });
        } else {
          console.log(`Activating genre ${id} with no active children`);
          if (genre.children.length > 0) {
            for (let i = 0; i < genre.children.length; i += 1) {
              // this.activeTags.push(genre.children[i].id);
            }
          }
          this.activeTags.push(id);
        }
      }

      // 3.3. Handle genres with neither parents nor children
      else {
        this.activeTags.push(id);
      }
    }

    // 4. Handle tag activation logic
    else {
      this.activeTags.push(id);
    }

    // if (parentGenre != null && parentGenre.children != null) {
    //   let siblingsActive = null;
    //   if (parentGenre != null && parentGenre.children != null) {
    //     const siblings = parentGenre.children;
    //     for (let i = 0; i < siblings.length; i += 1) {
    //       const sibling = siblings[i];
    //       if (sibling != null) {
    //         if (this.activeTags.indexOf(sibling.id) === -1) {
    //           siblingsActive = true;
    //         }
    //       }
    //     }
    //   }
    //   if (siblingsActive != null) {
    //     if (this.activeTags.indexOf(id) === -1) {
    //       const subgenre: ?Tag = parentGenre.children.flatMap(g => g)
    //       .filter(t => t.id === id)[0];
    //       console.log(subgenre.tag_name);
    //       //this.logEvent('subgenre', 'selected', `${subgenre.tag_name}`);
    //     }
    //   }
    // }

    if (this.activeTags.length > 0) {
      this.searchType = "search";
      this.clearAiSearchState();
      this.refreshSearchPage();
    } else {
      this.resetKeywords();
    }

    const tag = this.tags.filter((t) => t.id === id)[0];
    if (isActive === false) {
      if (tag != null) {
        // console.log(tag.tag_name);
        this.logEvent("keyword", "selected", `${tag.tag_name}`);
      }
      if (genre != null) {
        // console.log(genre.tag_name);
        this.logEvent("genre", "selected", `${genre.tag_name}`);
      }
    }
    // console.log("this.activeTags", this.activeTags);
  };

  toggleSubgenre = (element, id) => {
    /** @type {Tag | null} */ const genre = this.genres
      .flatMap((g) => g)
      .map((t) => (t.children ? t.children.filter((c) => c.id === id) : []))
      .filter((a) => a)
      .flatMap((n) => n)[0];
    if (this.isTagActive(id) && genre) {
      this.logEvent("subgenre", "selected", `${genre.tag_name}`);
    }
  };

  saveSearch = () => {
    if (!this.loggedIn) {
      this.showLoginFlow();
      return;
    }
    this.myAccountState.toSaveSearch(
      this.visibleMusic.map((m) => m.music.id.toString()),
      true,
    );
    this.showingMyAccount = true;
  };

  /**
   * @action
   * @param {string} trackId
   */
  saveSingleTrackToNewSearch = (trackId) => {
    this.myAccountState.toSaveSearch([trackId], false);
    this.showingMyAccount = true;
  };

  viewAllLikes = () => {
    // 'genre_ids' and 'keyword_ids' get immediately merged on the backend anyways, so the fact
    // that these are two separate values should just be removed
    const likes = SavedSet.getSetContents("tracks");

    if (likes.length === 0) {
      alert("You have not liked any tracks yet");
      return;
    }

    /** @type {SavedPlaylistPost} */ const savedPlaylist = {
      tracks: likes,
    };
    // console.log(`Saving likes: ${JSON.stringify(savedPlaylist, null, 2)}`);

    retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id === -1) {
          alert("Failed to save favorites. Please try again.");
          return;
        }
        const url = `/en/favorites/${r.id}`;
        window.history.pushState({}, "", url);
        this.refreshSearchFromURL();
      });
  };

  setShortlinkType = (type) => {
    this.shortLinkType = type;
  };

  setShortlinkTrackName = (name) => {
    this.shortLinkTrackName = name;
  };

  getShortLink = (urlToShare) => {
    if (urlToShare.target) {
      urlToShare = `${window.location.href.split("#")[0]}#likes=0&shared=1`;
    }

    fetch(`${SHORTENER_BASE_URL}/shorten/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: urlToShare }),
      cache: DISABLE_CACHEING ? "reload" : "default",
    })
      .then((r) => r.json())
      .then((r) => {
        this.shortLink = r.shortUrl;
        this.logEvent("shortlink", "created-shortlink", `${r.shortUrl}`);
      });
  };

  clearShortLink = () => {
    this.shortLink = null;
    this.shortLinkTrackName = null;
    this.shortLinkType = "Playlist";
  };

  /**
   * @action
   * @param {() => void} callback
   */
  acceptTOS = (callback) => {
    console.log("tos");
    if (!this.acceptedTOS) {
      this.tosCallback = callback;
    } else {
      callback();
    }
  };

  confirmTOS = () => {
    this.acceptedTOS = true;
    if (this.tosCallback != null) {
      this.tosCallback();
      this.tosCallback = null;
    }
  };

  rejectTOS = () => {
    this.tosCallback = null;
  };

  showingInstructions = () =>
    this.visibleMusic.length === 0 &&
    this.activeTags.length === 0 &&
    this.searchType !== "keyword_search" &&
    this.searchType !== "untagged" &&
    !this.loadingMusic;

  showMusicTypesPopup = (musicTypesCallback) => {
    this.musicTypesCallback = (type) => {
      this.closeMusicTypesPopup();
      musicTypesCallback(type);
    };
  };

  closeMusicTypesPopup = () => {
    this.musicTypesCallback = null;
  };

  /**
   * @action
   * @param {string} message
   * @param {() => void} callback
   */
  showConfirmActionCallback = (message, callback) => {
    this.confirmActionMessage = message;
    this.confirmActionCallback = () => {
      this.confirmActionCallback = null;
      callback();
    };
  };

  closeConfirmActionPopup = () => {
    this.confirmActionCallback = null;
  };

  /**
   * The user has clicked a play button, which may trigger us showing and then hiding the tip.
   */
  onClickedPlay = () => {
    this.clickedPlayCount += 1;
    this.showPlaybackTip = false;
    if (this.clickedPlayCount == 2) {
      // Get saved data from sessionStorage
      if (sessionStorage.getItem("tip::playback_tip") == null) {
        // Save data to sessionStorage
        // if (this.clickedPlayCount == 5) {
        sessionStorage.setItem("tip::playback_tip", "true");
        // }
        this.showPlaybackTip = true;
      }
    }
    this.cancelDrag();
  };

  hideSyncTip = () => {
    clearTimeout(this.syncTipTimeout);
    this.showSyncTip = false;
    const tipKey = "tip::videosynctip";
    // Get saved data from sessionStorage
    if (sessionStorage.getItem(tipKey) == null) {
      // Save data to sessionStorage
      sessionStorage.setItem(tipKey, "true");
    }
  };

  triggerSyncTip = () => {
    this.showSyncTip = true;
    try {
      clearTimeout(this.syncTipTimeout);
    } catch (e) {}
    this.syncTipTimeout = setTimeout(() => {
      console.log("clearing sync tip");
      this.hideSyncTip();
    }, 30000);
  };

  hidePlaybackTip = () => {
    this.showPlaybackTip = false;
  };

  hideSyncButtonTip = () => {
    this.showingSyncButtonTip = false;
    this.setTipButtonCount(15);
  };

  setTipButtonCount = (count) => {
    this.syncButtonTipCount = count;
  };

  triggerSyncButtonTip = () => {
    const tipKey = "tip::syncbuttontip";
    this.showingSyncButtonTip = false;
    if (sessionStorage.getItem(tipKey) == null) {
      this.changeSyncButtonTipCount();
      if (this.syncButtonTipCount > 9 && this.syncButtonTipCount < 15) {
        this.showingSyncButtonTip = true;
        if (this.syncButtonTipCount == 14) {
          sessionStorage.setItem(tipKey, "true");
        }
      }
    }
  };

  hideFavoritesTip = () => {
    this.showFavoritesTip = false;
    const tipKey = "tip::favoritestip";

    // Get saved data from sessionStorage
    if (sessionStorage.getItem(tipKey) == null) {
      // Save data to sessionStorage
      if (this.favoritesTipCount == 5) {
        sessionStorage.setItem(tipKey, "true");
      }
    }
  };

  hideListTip = () => {
    this.showListTip = false;
    const tipKey = "tip::listtip";

    // Get saved data from sessionStorage
    if (sessionStorage.getItem(tipKey) == null) {
      // Save data to sessionStorage
      sessionStorage.setItem(tipKey, "true");
    }
  };

  triggerListTip = () => {
    const tipKey = "tip::listtip";
    this.showListTip = false;

    if (sessionStorage.getItem(tipKey) == null) {
      sessionStorage.setItem(tipKey, "true");
      this.showListTip = true;
    }
  };

  /**
   * @action
   * @param {string} tip
   * @param {boolean} isFavoritesTip
   * @param {boolean} isKeywordsTip
   */
  showTip = (tip, isFavoritesTip, isKeywordsTip) => {
    const tipKey = `tip::${tip}`;
    this.showFavoritesTip = false;
    this.showKeywordsTip = false;

    // Get saved data from sessionStorage
    if (sessionStorage.getItem(tipKey) == null) {
      // Save data to sessionStorage
      if (isKeywordsTip === true) {
        this.changeKeywordsTipCount();
        this.showKeywordsTip = true;
        if (this.keywordsTipCount == 4) {
          sessionStorage.setItem(tipKey, "true");
        }
        return;
      }
      if (isFavoritesTip === true) {
        this.changeFavoritesTipCount();
        if (this.favoritesTipCount == 2) {
          this.showFavoritesTip = true;
          // this.tipMessage = tip;
          sessionStorage.setItem(tipKey, "true");
        }
      } else {
        sessionStorage.setItem(tipKey, "true");
        this.tipMessage = tip;
      }
    }

    // console.log(this.favoritesTipCount);
  };

  closeTip = () => {
    this.tipMessage = null;
  };

  changeSyncButtonTipCount = () => {
    this.syncButtonTipCount++;
  };

  changeFavoritesTipCount = () => {
    this.favoritesTipCount++;
  };

  changeKeywordsTipCount = () => {
    this.keywordsTipCount++;
  };

  showKeywordDefinitions = () => {
    this.showingKeywordDefinitions = true;
  };

  hideKeywordDefinitions = () => {
    this.showingKeywordDefinitions = false;
  };

  showVideoSyncError = () => {
    this.showingVideoSyncError = true;
  };

  hideVideoSyncError = () => {
    this.showingVideoSyncError = false;
  };

  downloadAll = () => {
    if (!this.loggedIn) {
      this.showLoginFlow();
      return;
    }
    // Even though each music.download() call also calls acceptTOS(), we wrap it here so that
    // if people reject the TOS, they don't have to reject it over and over again.
    this.acceptTOS(() => {
      this.showMusicTypesPopup((musicType) => {
        /** @type {HTMLAnchorElement} */
        const linkElement = document.createElement("a");
        linkElement.href = `${API_BASE_URL}/api/v1/download_playlist?type=${musicType}&id=${this.searchId}`;
        linkElement.download = `Asche & Spencer - Favorites - ${new Date()
          .toLocaleString()
          .replace(/:/g, "-")
          .replace(/\//g, "-")}.zip`;
        linkElement.style = "display: none";
        document.body.appendChild(linkElement);
        linkElement.click();
        linkElement.remove();
        this.logEvent(
          `zip-file-${musicType}`,
          "downloaded",
          "zip file download",
        );
      });
    });
  };

  /**
   * Refreshes the search page by updating music results and saving the current search query
   * @action
   */
  refreshSearchPage = () => {
    this.refreshMusic();

    // Save the contents of the search

    const searchQuery = this.formatSearchQuery();
    /** @type {SavedSearchPost} */ const saveSearch = {
      query: searchQuery,
    };

    retryFetch(`${API_BASE_URL}/api/v1/saved_search`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(saveSearch),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        runInAction(() => {
          this.searchId = r.id;
          let url = `/en/search/${r.id}`;
          if (this.sortDirection != null) {
            url += `/${this.sortDirection}`;
          }
          window.history.pushState({}, "", url);
        });
      });
  };

  refreshSearchFromURL = () => {
    let newLanguage = "en";
    let newSearchType = "search";
    let newSearchId = "";
    let newSortDirection = null;
    const parts = window.location.href.split("?")[0].split("#")[0].split("/");

    this.enableHearts = true;
    this.playlistName = null;
    this.playlistOriginalTrackIds = null;

    if (window.location.hash) {
      let hash = window.location.hash.substring(1);

      if (hash.includes("mobile", 0) === true) {
        this.mobileActive = false;
      }
      if (hash.includes("tutorial-", 0) === true) {
        hash = hash.replace("tutorial-", "");
        const videoIndex = parseInt(hash.split("--")[1]);
        if (Number.isInteger(videoIndex)) {
          this.showSpecificVideoTutorial(videoIndex);
        }
      } else if (hash.includes("login", 0) === true) {
        this.loginReferrer = true;
      } else {
        console.log("Ignoring URL hash");
      }
    }

    if (
      parts.length >= 5 &&
      (parts[4] === "keyword_search" || parts[4] === "single_keyword_search")
    ) {
      newLanguage = parts[3];
      if (parts.length >= 6 && (parts[5] === "asc" || parts[5] === "desc")) {
        newSortDirection = parts[5];
      } else {
        newSortDirection = null;
      }
      newSearchType = parts[4];
      /** @type {string | null} */
      const params = window.location.href.split("?")[1].replace("#mobile", "");
      if (params != null) {
        newSearchId = decodeURIComponent(params);
        this.searchBarContents = newSearchId;
        this.refreshSearchBarAutocomplete();
      }
    }

    // AI natural language search
    if (parts.length >= 5 && parts[4] === "ai_search") {
      newLanguage = parts[3];
      if (parts.length >= 6 && (parts[5] === "asc" || parts[5] === "desc")) {
        newSortDirection = parts[5];
      } else {
        newSortDirection = null;
      }
      newSearchType = "ai_search";
      const params = window.location.href.split("?")[1]?.replace("#mobile", "");
      if (params != null) {
        newSearchId = decodeURIComponent(params);
        this.aiSearchQuery = newSearchId;
      }
    }

    if (parts.length >= 5 && parts[4] === "untagged") {
      if (parts.length >= 6) newSortDirection = parts[5];
      newSearchType = "untagged";
      this.searchBarContents = "";
    }
    if (parts.length >= 5 && parts[4] === "all_disabled") {
      if (parts.length >= 6) newSortDirection = parts[5];
      newSearchType = "all_disabled";
      this.searchBarContents = "";
    }
    if (parts.length >= 6 && parts[4] === "search") {
      newLanguage = parts[3];
      if (parts.length >= 6) newSearchId = parts[5];
      if (parts.length >= 7) newSortDirection = parts[6];
      newSearchType = "search";
      this.searchBarContents = "";
    }
    if (parts.length >= 6 && parts[4] === "favorites") {
      newLanguage = parts[3];
      if (parts.length >= 6) newSearchId = parts[5];
      if (parts.length >= 7) newSortDirection = parts[6];
      newSearchType = "favorites";
      this.searchBarContents = "";
      this.mobileActive = false;
    }
    if (parts.length >= 6 && parts[4] === "category") {
      newLanguage = parts[3];
      if (parts.length >= 6) newSearchId = parts[5];
      if (parts.length >= 7) newSortDirection = parts[6];
      newSearchType = "category";
      this.searchBarContents = "";
    }
    if (parts.length >= 6 && parts[4] === "reset_password") {
      newLanguage = parts[3];
      newSearchType = "reset_password";
      this.searchBarContents = "";
      this.showingLoginFlow = true;
      this.loginFlowState.currentPage = "change-password";
      this.loginFlowState.passwordResetLink = parts[5];
    }
    if (
      (parts.length === 5 && parts[4] === "favorites") ||
      (newSearchId.length === 0 &&
        newSearchType !== "untagged" &&
        newSearchType !== "all_disabled")
    ) {
      this.resetKeywords();
      return;
    }

    if (
      newLanguage !== this.language ||
      newSearchId !== this.searchId ||
      newSearchType !== this.searchType ||
      newSortDirection !== this.sortDirection
    ) {
      // Reset selection and currently playing values on a new search
      if (MusicTrackState.globallyActiveTrack != null) {
        const active = MusicTrackState.globallyActiveTrack;
        if (active.playing) active.pause();
        MusicTrackState.globallyActiveTrack = null;
      }
      this.timeline.reset();
      this.selectedTrack = null;

      this.language = newLanguage;
      this.searchId = newSearchId;
      this.searchType = newSearchType;
      this.sortDirection = newSortDirection;

      // Clear AI search state when navigating away from AI search
      if (newSearchType !== "ai_search") {
        this.clearAiSearchState();
      }

      // Cancel any in-flight loads prior to loading a new page of music
      for (let i = 0; i < this.music.length; i += 1) {
        this.music[i].cancelAnyInFlightLoad();
      }

      if (this.searchType === "search") {
        retryFetch(`${API_BASE_URL}/api/v1/saved_search?id=${this.searchId}`, {
          method: "GET",
          cache: DISABLE_CACHEING ? "reload" : "default",
          headers: new Headers({ "content-type": "application/json" }),
          mode: "cors",
          withCredentials: true,
          credentials: "include",
        })
          .then((r) => r.json())
          .then((r) => {
            runInAction(() => {
              if (r.success) {
                if (r.tagsAndGenres) {
                  const tagIds = this.tags
                    .filter((t) => r.tagsAndGenres.indexOf(t.urlsafe_name) !== -1)
                    .map((t) => t.id);
                  const genreIds = this.genres
                    .flatMap((g) => g)
                    .flatMap((g) =>
                      [g].concat(g.children != null ? g.children : []),
                    )
                    .filter((g) => r.tagsAndGenres.indexOf(g.urlsafe_name) !== -1)
                    .map((g) => g.id);
                  this.activeTags = tagIds.concat(genreIds);
                }
                this.refreshMusic();
              } else {
                this.resetKeywords();
              }
            });
          });
      } else if (
        this.searchType === "favorites" ||
        this.searchType === "category" ||
        this.searchType === "keyword_search" ||
        this.searchType === "single_keyword_search" ||
        this.searchType === "untagged" ||
        this.searchType === "all_disabled"
      ) {
        this.activeTags = [];
        this.refreshMusic();
      } else if (this.searchType === "ai_search") {
        this.activeTags = [];
        this.performAiSearch();
      }
    } else if (window.location.hash) {
      console.log("Ignoring search - showing tutorial video");
    } else {
      console.log(`Ignoring search for ${this.searchId}`);
    }
  };

  /**
   * @action
   * @param {"asc" | "desc" | null} sortDirection
   */
  setSortDirection = (sortDirection) => {
    if (this.sortDirection === sortDirection) this.setSortDirectionURL(null);
    this.setSortDirectionURL(sortDirection);
  };

  toggleSortDirection = () => {
    if (this.sortDirection == null) this.setSortDirectionURL("asc");
    else if (this.sortDirection === "asc") this.setSortDirectionURL("desc");
    else if (this.sortDirection === "desc") this.setSortDirectionURL(null);
  };

  /**
   * @action
   * @param {"asc" | "desc" | null} sortDirection
   */
  setSortDirectionURL = (sortDirection) => {
    let newUrl;
    if (
      this.searchType === "keyword_search" ||
      this.searchType === "single_keyword_search"
    ) {
      newUrl = `/${this.language}/${this.searchType}/${
        sortDirection != null ? sortDirection : ""
      }?${encodeURIComponent(this.searchId)}`;
    } else if (this.searchType === "ai_search") {
      newUrl = `/${this.language}/ai_search/${
        sortDirection != null ? sortDirection : ""
      }?${encodeURIComponent(this.aiSearchQuery)}`;
    } else {
      newUrl = `/${this.language}/${this.searchType}/${this.searchId}${
        sortDirection != null ? `/${sortDirection}` : ""
      }`;
    }
    window.history.pushState({}, "", newUrl);

    this.refreshSearchFromURL();
  };

  /**
   * @action
   * @param {string} category
   */
  setCategory = (category) => {
    if (
      this.searchType === "category" &&
      this.searchId.toString() === category.toString()
    ) {
      if (this.music.length > 0) {
        const activeTrack = MusicTrackState.globallyActiveTrack;
        if (activeTrack != null) {
          activeTrack.pause();
          this.timeline.reset();
        }
        this.selectTrack(this.music[0]);
        this.music[0].ensureGloballyActive();
      }
    } else {
      const newUrl = `/${this.language}/category/${category}${
        this.sortDirection != null ? `/${this.sortDirection}` : ""
      }`;
      window.history.pushState({}, "", newUrl);

      this.refreshSearchFromURL();
    }
  };

  viewUntagged = () => {
    const newUrl = `/${this.language}/untagged${
      this.sortDirection != null ? `/${this.sortDirection}` : ""
    }`;
    window.history.pushState({}, "", newUrl);

    this.refreshSearchFromURL();
  };

  viewAllDisabled = () => {
    const newUrl = `/${this.language}/all_disabled${
      this.sortDirection != null ? `/${this.sortDirection}` : ""
    }`;
    window.history.pushState({}, "", newUrl);

    this.refreshSearchFromURL();
  };

  /**
   * This creates a SearchPost query object from our current state. This is useful for making
   * queries directly and for saving the current query.
   *
   * @returns {SearchPost}
   */
  formatSearchQuery = () => {
    if (this.searchType === "search") {
      const flatGenresList = this.genres
        .flatMap((g) => g)
        .flatMap((g) => {
          if (g.children != null) return [g].concat(g.children);
          return [g];
        });
      const flatTagsList = this.tags;

      /** @type {SearchPost} */ const searchQuery = {
        searchType: "search",
        sortDirection: this.sortDirection,
        tagsAndGenres: flatGenresList
          .filter((genre) => this.activeTags.indexOf(genre.id) !== -1)
          .map((t) => t.urlsafe_name)
          .concat(
            flatTagsList
              .filter((tag) => this.activeTags.indexOf(tag.id) !== -1)
              .map((t) => t.urlsafe_name),
          ),
      };

      this.hasActiveGenres =
        flatGenresList
          .filter((genre) => this.activeTags.indexOf(genre.id) !== -1)
          .map((t) => t.tag_name).length > 0;

      this.hasActiveKeywords =
        flatTagsList
          .filter((keyword) => this.activeTags.indexOf(keyword.id) !== -1)
          .map((t) => t.tag_name).length > 0;

      // console.log('formatting search query', this.hasActiveGenres);
      return searchQuery;
    }
    if (this.searchType === "favorites") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "favorites",
        sortDirection: this.sortDirection,
        favorites: this.searchId,
      };

      return searchQuery;
    }
    if (this.searchType === "category") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "category",
        sortDirection: this.sortDirection,
        category: this.searchId,
      };

      return searchQuery;
    }
    if (this.searchType === "keyword_search") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "keyword_search",
        sortDirection: this.sortDirection,
        query: this.searchId,
      };

      return searchQuery;
    }
    if (this.searchType === "single_keyword_search") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "single_keyword_search",
        sortDirection: this.sortDirection,
        query: this.searchId,
      };

      return searchQuery;
    }
    if (this.searchType === "untagged") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "untagged",
        sortDirection: this.sortDirection,
      };

      return searchQuery;
    }
    if (this.searchType === "all_disabled") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "all_disabled",
        sortDirection: this.sortDirection,
      };

      return searchQuery;
    }
    if (this.searchType === "similar") {
      /** @type {SearchPost} */ const searchQuery = {
        searchType: "similar",
        sortDirection: this.sortDirection,
        trackId: this.searchingSimilarTrack.music.id,
        highestTags: this.highestTags,
      };
      return searchQuery;
    }
    if (this.searchType === "ai_search") {
      // AI search is handled separately via performAiSearch()
      return null;
    }

    this.highestTags = null;

    // By default create an empty search

    /** @type {SearchPost} */ const searchQuery = {
      searchType: "search",
      tagsAndGenres: [],
    };

    return searchQuery;
  };

  hasAValue = (value) => {
    if (value === null || value === "" || isNaN(value)) {
      return false;
    }
    return true;
  };

  loadNextPageOfMusic = () => {
    if (!this.hasMoreMusic) {
      // || this.loadingMusic
      return Promise.resolve();
    }

    // AI search loads all results at once, no pagination needed
    if (this.searchType === "ai_search") {
      return Promise.resolve();
    }

    const searchQuery = this.formatSearchQuery();

    const loadingMusicNonce = this.loadingMusicNonce + 1;
    this.loadingMusicNonce = loadingMusicNonce;
    this.loadingMusic = true;

    // Load the contents of the search
    let musicPageSize = Math.min(
      Math.max(MIN_MUSIC_PAGE_SIZE, this.music.length),
      MAX_MUSIC_PAGE_SIZE,
    );
    if (this.searchType === "similar") musicPageSize = null;

    return retryFetch(`${API_BASE_URL}/api/v1/search`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(
        Object.assign({}, searchQuery, {
          offset: this.music.length,
          pageSize: musicPageSize,
        }),
      ),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((music) => {
        runInAction(() => {
          this.playlistOwner = music.playlistOwner;
          this.playlistName = music.playlistName;
          this.linkedVideoId = music.linked_video_id;

          if (music.playlistData != null) {
            this.savedPlaylistData = music.playlistData
              ? music.playlistData
              : {};
          } else {
            this.savedPlaylistData = {};
          }

          if (this.playlistName) {
            this.setMusicVolume(
              this.hasAValue(this.savedPlaylistData.musicVolume)
                ? this.savedPlaylistData.musicVolume
                : this.musicVolume,
            );

            this.videoSyncState.setVideoAudioLevel(
              this.hasAValue(this.savedPlaylistData.videoVolume)
                ? this.savedPlaylistData.videoVolume
                : this.videoSyncState.videoAudioLevel,
            );
          }

          if (!this.savedPlaylistData.hasOwnProperty("tracks")) {
            this.savedPlaylistData.tracks = [];
          }

          let videoAnchorPercentage = 0;
          if (this.savedPlaylistData.hasOwnProperty("videoAnchorPercentage")) {
            videoAnchorPercentage = this.savedPlaylistData.videoAnchorPercentage;
          }
          if (this.timeline) {
            this.savedPlaylistData.videoAnchorPercentage = videoAnchorPercentage;
          }

          this.savePlaylistCuePoints(music.tracks);

          if (
            !this.isAdmin &&
            music.hasPassword &&
            window.localStorage.getItem(`playlist-${this.searchId}`) == null
          ) {
            this.showPasswordPrompt();
          }
          if (this.linkedVideoId) {
            this.getVideoFromPlaylist(this.linkedVideoId);
          }
          // If we didn't get preempted by another load in flight, and we got non-empty results
          if (this.loadingMusicNonce === loadingMusicNonce) {
            if (this.searchType === "similar" && music.tracks) {
              this.musicCount = music.tracks.length;
            }
            music.tracks.forEach((m) => {
              if (this.music.filter((n) => n.music.id === m.id).length > 0) {
              } else if (!this.isLicensedExclusive(m)) {
                const trackState = MusicTrackState.getMusicTrackState(m, this);
                this.music.push(trackState);
              } else {
                this.musicCount = this.musicCount - 1;
              }
            });
            if (this.music.length === 0) this.musicCount = 0;
            if (this.music.length > 0) {
              this.music[0].ensureLoaded();
            }
            this.loadingMusic = false;
            this.hasMoreMusic =
              music.tracks.length === musicPageSize &&
              this.searchType !== "favorites";
            
            // For saved favorites playlists, store the original track IDs from the server.
            // This prevents multi-tab localStorage changes from breaking the "save playlist" button.
            if (this.searchType === "favorites" && this.playlistName != null) {
              this.playlistOriginalTrackIds = this.music.map((m) => m.music.id.toString());
            }
            
            this.refreshLikes();
          }
        });
      });
  };

  /**
   * @action
   * @param {number} videoId
   */
  getVideoFromPlaylist = (videoId) => {
    retryFetch(`${API_BASE_URL}/api/v1/video?videoId=${videoId}`, {
      method: "GET",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.success) {
          const video = this.videoSyncState.cleanVideoDetails(r.video);
          this.videoSyncState.setVideo(video.url, video.name);
        }
      });

    this.showVideoSync();
  };

  saveVideoDataToPlaylist = () => {
    const savedPlaylistData = {
      playlistId: this.searchId,
      playlistData: this.savedPlaylistData,
    };

    //console.log(savedPlaylistData);

    retryFetch(`${API_BASE_URL}/api/v1/save-playlist-data`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylistData),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        // console.log(response);
        if (response.success) {
          console.log("saved");
        } else {
          console.log("not saved");
          alert("Failed to save playlist data. Please try again.");
        }
      });
  };

  savePlaylistCuePoints = (music) => {
    music.forEach((m) => {
      if (Object.keys(this.savedPlaylistData).length === 0) return;
      const currentSavedTrack = this.savedPlaylistData.tracks.find(
        (track) => track.id === m.id,
      );
      if (!currentSavedTrack) {
        this.savedPlaylistData.tracks.push({
          id: m.id,
          audioOffset: 0,
          name: m.name,
        });
      }
    });
  };

  setVideoAnchorPercentage = (percentage) => {
    if (
      Object.keys(this.savedPlaylistData).length &&
      this.savedPlaylistData.hasOwnProperty("videoAnchorPercentage")
    ) {
      this.savedPlaylistData.videoAnchorPercentage = percentage;
    }
  };

  changePlaylistVideoSyncData = (trackId, { ...options }) => {
    // if (!this.playlistName) {
    //   return;
    // }
    if (!this.savedPlaylistData.hasOwnProperty("tracks") && this.music) {
      const trackIds = this.music.map((track) => ({
        id: track.music.id,
      }));
      this.savePlaylistCuePoints(trackIds);
    }
    const track = this.savedPlaylistData.tracks.find((n) => n.id === trackId);
    const changes = Object.assign({}, track, options);
    const foundIndex = this.savedPlaylistData.tracks.findIndex(
      (n) => n.id == trackId,
    );

    if (foundIndex > -1) {
      this.savedPlaylistData.tracks[foundIndex] = changes;
    }

    if (options.videoAnchorPercentage) {
      this.savedPlaylistData.videoAnchorPercentage =
        options.videoAnchorPercentage;
    }
    // console.log("ssfefw", this.savedPlaylistData.tracks[foundIndex]);
  };

  /**
   * @observable
   * @param {MusicSql} track
   */
  isLicensedExclusive = (track) => {
    if (this.isAdmin) return false;
    if (track.exclusive === true) return true;
    if (track.license_details_json != null) {
      try {
        const licenses = JSON.parse(track.license_details_json);
        if (!licenses) return false;
        const time = new Date().getTime();
        for (let i = 0; i < licenses.length; i += 1) {
          const l = licenses[i];
          if (new Date(l.expiration).getTime() > time && l.exclusive) {
            return true;
          }
        }
      } catch (e) {}
    }
    return false;
  };

  /**
   * Refreshes the music list based on current search parameters
   * @action
   */
  refreshMusic = () => {
    this.activeTagsWithSubgenres = this.getActiveWithSubGenres();
    const searchQuery = this.formatSearchQuery();

    this.hasMoreMusic = true;
    this.firstLoad = false;
    this.musicCount = null;

    // Reset selection and currently playing values on a new search
    if (MusicTrackState.globallyActiveTrack != null) {
      const active = MusicTrackState.globallyActiveTrack;
      if (active.playing) active.pause();
      this.timeline.reset();
      MusicTrackState.globallyActiveTrack = null;
    }
    this.selectedTrack = null;

    // Cancel any in-flight loads prior to loading a new page of music
    for (let i = 0; i < this.music.length; i += 1) {
      this.music[i].cancelAnyInFlightLoad();
      this.music[i].unload();
    }

    this.music = [];

    const loadingMusicCountNonce = this.loadingMusicCountNonce + 1;
    this.loadingMusicCountNonce = loadingMusicCountNonce;

    // Load the count
    // console.log(searchQuery);
    try {
      retryFetch(`${API_BASE_URL}/api/v1/search`, {
        method: "POST",
        cache: DISABLE_CACHEING ? "reload" : "default",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(Object.assign({}, searchQuery, { count: true })),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((music) => {
          runInAction(() => {
            if (loadingMusicCountNonce === this.loadingMusicCountNonce) {
              this.musicCount = music.count;
            }
          });
        });
    } catch (err) {
      console.error(err);
    }

    this.loadNextPageOfMusic().then(() => {
      this.loadNextPageOfMusic();
    });
  };

  searchTopTags = (track) => {
    const toBecomeActive = [];
    for (let i = 0; i < this.allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];

      const tagValue = track.music[tag.urlsafe_name];
      if (tagValue > 4) toBecomeActive.push(tag.id);
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 3) toBecomeActive.push(tag.id);
      }
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 2) toBecomeActive.push(tag.id);
      }
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 1) toBecomeActive.push(tag.id);
      }
    }
    this.activeTags = toBecomeActive;
    this.searchingLikeTrackTags = [];
    this.searchingLikeTrack = null;
    this.searchType = "search";
    this.clearAiSearchState();
    this.refreshSearchPage();
  };

  searchSimilarTracks = (track) => {
    const toBecomeActive = [];
    for (let i = 0; i < this.allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];

      const tagValue = track.music[tag.urlsafe_name];
      if (tagValue > 4) toBecomeActive.push(tag.urlsafe_name);
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 3) toBecomeActive.push(tag.urlsafe_name);
      }
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 2) toBecomeActive.push(tag.urlsafe_name);
      }
    }
    if (toBecomeActive.length === 0) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.music[tag.urlsafe_name];
        if (tagValue > 1) toBecomeActive.push(tag.urlsafe_name);
      }
    }

    if (toBecomeActive.length === 0 && track.music.is_version && track.parent) {
      for (let i = 0; i < this.allTags.length; i += 1) {
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = track.parent.music[tag.urlsafe_name];
        if (tagValue > 4) toBecomeActive.push(tag.urlsafe_name);
      }
      if (toBecomeActive.length === 0) {
        for (let i = 0; i < this.allTags.length; i += 1) {
          /** @type {Tag} */
          const tag = allTags[i];

          const tagValue = track.parent.music[tag.urlsafe_name];
          if (tagValue > 3) toBecomeActive.push(tag.urlsafe_name);
        }
      }
      if (toBecomeActive.length === 0) {
        for (let i = 0; i < this.allTags.length; i += 1) {
          /** @type {Tag} */
          const tag = allTags[i];

          const tagValue = track.parent.music[tag.urlsafe_name];
          if (tagValue > 2) toBecomeActive.push(tag.urlsafe_name);
        }
      }
    }

    this.activeTags = [];
    this.searchingLikeTrackTags = [];
    this.searchingSimilarTrack = track;
    this.searchType = "similar";
    this.highestTags = toBecomeActive;
    this.clearAiSearchState();
    this.refreshSearchPage();
  };

  /**
   * This opens the search window to search for music like a selected track.
   */
  showRelevantTags = (track) => {
    this.searchingLikeTrack = track;
    this.searchingLikeTrackTags = [].concat(this.activeTags);
  };

  /**
   * @action
   * @param {string} tagId
   */
  toggleSearchLikeTrackTag = (tagId) => {
    if (this.searchLikeTrackTagActive(tagId)) {
      this.searchingLikeTrackTags.splice(
        this.searchingLikeTrackTags.indexOf(tagId),
        1,
      );
    } else {
      this.searchingLikeTrackTags.push(tagId);
    }
  };

  /**
   * @observable
   * @param {string} tagId
   */
  searchLikeTrackTagActive = (tagId) =>
    this.searchingLikeTrackTags.indexOf(tagId) !== -1;

  commitSearchLikeTrack = () => {
    this.activeTags = this.searchingLikeTrackTags;
    this.searchingLikeTrackTags = [];
    this.searchingLikeTrack = null;
    this.searchType = "search";
    this.clearAiSearchState();
    this.refreshSearchPage();
  };

  toggleMobileMenu = () => {
    this.mobileActive = !this.mobileActive;
    this.showingPlaylistListing = false;
    this.menuOpen = false;
    setTimeout(() => {
      const scrollEvent = new Event("scroll");
      window.dispatchEvent(scrollEvent);
    }, 10);
  };

  /**
   * @observable
   * @param {string} id
   */
  isTagActive = (id) => {
    if (this.activeTags.indexOf(id) > -1) {
      // Check if all of our siblings are also active

      const parentGenre = this.genres
        .flatMap((g) => g)
        .filter(
          (g) =>
            g.children != null &&
            g.children.filter((c) => c.id === id).length > 0,
        )[0];
      if (parentGenre != null && parentGenre.children != null) {
        const siblings = parentGenre.children;
        let allSiblingsActive = true;
        for (let i = 0; i < siblings.length; i += 1) {
          const sibling = siblings[i];
          if (this.activeTags.indexOf(sibling.id) === -1) {
            allSiblingsActive = false;
          }
        }
        if (this.activeTags.indexOf(parentGenre.id) === -1) {
          allSiblingsActive = false;
        }

        if (allSiblingsActive) return false;
      }

      // If we're not in the special case of all active siblings, return true

      return true;
    }

    // Check if we're the parent of an active tag, then always return true

    /** @type {Tag | null} */ const genre = this.genres
      .flatMap((g) => g)
      .filter((g) => g.id === id)[0];
    if (genre != null && genre.children != null) {
      /** @type {Array<string>} */ const childIds = genre.children.map(
        (t) => t.id,
      );
      for (let i = 0; i < childIds.length; i += 1) {
        const childId = childIds[i];
        if (this.activeTags.indexOf(childId) !== -1) {
          return true;
        }
      }
    }

    // All others are false

    return false;
  };

  /**
   * @observable
   */
  getGenresWithSubgenres = () => {
    const allGenres = [];
    const genresFlat = this.genres.flatMap((r) => r);

    for (let i = 0; i < genresFlat.length; i += 1) {
      /** @type {Tag} */
      const genre = genresFlat[i];
      allGenres.push(genre);
      if (genre.children != null) {
        for (let ii = 0; ii < genre.children.length; ii += 1) {
          allGenres.push(genre.children[ii]);
        }
      }
    }
    return allGenres;
  };

  /**
   * @observable
   */
  getActiveWithSubGenres = () => {
    const withSubgenres = [];
    const genresFlat = this.genres.flatMap((r) => r);

    for (let i = 0; i < genresFlat.length; i += 1) {
      /** @type {Tag} */
      const genre = genresFlat[i];
      if (genre.children != null && this.isTagActive(genre.id)) {
        withSubgenres.push(genre);
      } else if (this.genreChildrenPresent === false) {
        this.removeGenreChildrenPresent();
      }
    }
    return withSubgenres;
  };

  selectTrack = (track) => {
    // If we're selecting a new track, and the old track was loading the audio, cancel that request
    if (this.selectedTrack != null && track !== this.selectedTrack) {
      this.selectedTrack.cancelAnyInFlightLoad();
    }
    // Quietly load the track just in case we want to play it later
    track.ensureLoaded();

    this.selectedTrack = track;

    // Smoothly scroll a new page of tracks in, if we're off screen

    // 1. Find the track's current position

    const index = this.visibleMusic.indexOf(track);
    if (index === -1 && track.parent != null) {
      this.visibleMusic.indexOf(track.parent);
    }

    let foundTrackPosition = false;
    let top = 0;
    let height = 0;
    if (this.selectedTrack.containerDivRef != null) {
      foundTrackPosition = true;
      const trackRect =
        this.selectedTrack.containerDivRef.getBoundingClientRect();
      top = trackRect.top;
      height = trackRect.height;
    } else if (index !== -1 && this.musicContainerDiv != null) {
      foundTrackPosition = true;
      const containerRect = this.musicContainerDiv.getBoundingClientRect();
      top = containerRect.top + this.musicListScrollTop;
      for (let i = 0; i < index; i += 1) {
        top += this.visibleMusic[i].getHeight();
      }
      height = track.getHeight();
    }
    const bottom = top + height;

    // 2. Scroll to the track

    if (foundTrackPosition && !this.isMobileWindow) {
      const containerRect = this.musicContainerDiv.getBoundingClientRect();

      if (top < containerRect.top) {
        // Off screen to the top
        const currentTrackBottom = bottom;
        const desiredTrackBottom = window.innerHeight - 100;
        const distance = desiredTrackBottom - currentTrackBottom;
        console.log(distance);
        window.scrollTo({
          left: window.scrollX,
          top: window.scrollY - distance,
          behavior: "smooth",
        });
      }
      if (bottom > window.innerHeight) {
        // Off screen to the bottom
        const currentTrackTop = top;
        const desiredTrackTop = containerRect.top + 100;
        const distance = desiredTrackTop - currentTrackTop;
        window.scrollTo({
          left: window.scrollX,
          top: window.scrollY - distance,
          behavior: "smooth",
        });
      }
    }
  };

  isTrackSelected = (track) => this.selectedTrack === track;

  // ///////////////////////
  // Handle scrolling
  // ///////////////////////

  /** @type {HTMLDivElement | null} */
  rootContainer;

  /** @type {HTMLDivElement | null} */
  rootDiv;

  /** @type {HTMLDivElement | null} */
  aiSearchPanelRef;

  /** @type {HTMLDivElement | null} */
  musicDiv;

  /** @type {HTMLDivElement | null} */
  musicContainerDiv;

  setRootContainer = (rootContainer) => {
    this.rootContainer = rootContainer;
  };

  setRootDiv = (rootDiv) => {
    this.rootDiv = rootDiv;
  };

  setMusicDiv = (musicDiv) => {
    this.musicDiv = musicDiv;
    this.onResize();
  };

  setMusicContainerDiv = (musicContainerDiv) => {
    this.musicContainerDiv = musicContainerDiv;
  };

  showingLoadingTrack = () => this.loadingMusic || this.hasMoreMusic;

  getContentHeight = () => {
    let contentHeight = 0;
    for (let i = 0; i < this.visibleMusic.length; i += 1) {
      contentHeight += this.visibleMusic[i].getHeight();
    }
    if (this.showingLoadingTrack()) {
      contentHeight += LOADING_INDICATOR_HEIGHT;
    }
    return contentHeight;
  };

  calculateOffsetTop = (elem) => {
    if (elem === document.body) return 0;
    if (elem.style.position === "fixed" || elem.style.position === "absolute") {
      return elem.offsetTop + this.calculateOffsetTop(elem.parentNode);
    }
    return this.calculateOffsetTop(elem.parentNode);
  };

  recomputeMusicListSize = () => {
    if (this.musicContainerDiv != null) {
      const rect = this.musicContainerDiv.getBoundingClientRect();
      this.musicListHeight = window.innerHeight - rect.top;
      this.musicListWidth = rect.width;
    }
  };

  onResize = () => {
    this.recomputeMusicListSize();
    if (window.innerWidth < 1024) {
      this.isMobileWindow = true;
    } else {
      this.isMobileWindow = false;
    }
    this.isCompactLayout = window.innerWidth < 1220;
  };

  onScroll = () => {
    if (!this.isMobileWindow) {
      if (this.rootContainer != null && this.rootDiv != null) {
        const rect = this.rootContainer.getBoundingClientRect();
        let offsetY = rect.top;
        let musicScroll = 0;
        const offsetX = -window.scrollX;

        if (offsetY < 0 && !this.showingInstructions()) {
          musicScroll = offsetY;
          offsetY = 0;
          this.rootDiv.style.top = "0";
          this.rootDiv.style.position = "fixed";
          this.rootDiv.style.left = `${offsetX}px`;
          if (this.aiSearchPanelRef != null) {
            this.aiSearchPanelRef.style.position = "fixed";
            this.aiSearchPanelRef.style.top = "0";
          }
        } else {
          this.rootDiv.style.top = "0";
          this.rootDiv.style.left = "0"; // (offsetX - rect.left)+'px';
          this.rootDiv.style.position = "absolute";
          if (this.aiSearchPanelRef != null) {
            this.aiSearchPanelRef.style.position = "absolute";
            // Top is set inline by the render (triggerRect.bottom + 8);
            // clear the fixed override so the inline value takes effect.
            this.aiSearchPanelRef.style.top = "";
          }
        }

        if (this.musicDiv != null) this.musicDiv.style.top = `${musicScroll}px`;
        this.musicListScrollTop = musicScroll;

        if (offsetY >= 0) {
          // TODO: this seems to cause extra reflows, which slow down the page
          this.recomputeMusicListSize();
        }
      }
    }

    const fromBottom =
      document.documentElement.scrollHeight -
      document.documentElement.scrollTop -
      window.innerHeight;
    if (
      fromBottom < LOAD_NEXT_MUSIC_PAGE_PX &&
      this.hasMoreMusic &&
      !this.loadingMusic
    ) {
      this.loadNextPageOfMusic();
    }
  };
}

export default SearchState;
